import React, { memo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useObservable } from 'react-use-observable';

import { App, Button, Checkbox, Pagination, Table, Tooltip, Typography } from 'antd';

import { formatDate } from 'helpers/date';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import Truncate from 'v2/components/Shared/TruncateText';
import type { IAffiliate } from 'v2/interfaces/affiliate';
import affiliateService from 'v2/services/affiliate';

import { Icon } from '@eduzz/ui-antd-theme';

interface IProps {
  onSort: (sortField: string, sortDirection: 'asc' | 'desc') => void;
}

const TablePending = ({ onSort }: IProps) => {
  const { message, modal } = App.useApp();
  const [isLoading] = useObservable(() => affiliateService.isLoading(), []);
  const [affiliates] = useObservable(() => affiliateService.getAffiliates(), []);
  const [pagination] = useObservable(() => affiliateService.getPaginationResponse(), []);
  const [currentSort] = useObservable(() => affiliateService.getCurrentSort(), []);

  const [affiliatesSelected, setAffiliatesSelected] = useState<Array<number>>([]);

  const resetAffiliatesSelected = () => setAffiliatesSelected([]);

  const onChangeRowsPerPage = (perPage: number) => {
    affiliateService.setPagination({ ...pagination, ...{ per_page: perPage, current_page: 1 } });
  };

  const onChangePage = (page: number) => {
    affiliateService.setPagination({ ...pagination, ...{ current_page: page } });
  };

  const selectAllAffiliates = () => {
    const selected = affiliates.map(a => a.id);

    if (affiliates.length > affiliatesSelected.length) {
      setAffiliatesSelected(selected);
      return;
    }

    setAffiliatesSelected([]);
  };

  const toggleSelect = (id: number) => {
    if (affiliatesSelected.includes(id)) {
      setAffiliatesSelected(affiliatesSelected.filter(a => a !== id));
      return;
    }

    setAffiliatesSelected([...affiliatesSelected, id]);
  };

  const handleClickModerate = (id: number, status: 'A' | 'R') => {
    affiliateService.moderateAffiliate(id, status).subscribe(
      () => {
        setAffiliatesSelected([]);
        message.success(`Afiliado ${status === 'A' ? 'aprovado' : 'reprovado'} com sucesso.`);
      },
      () => message.error('Não foi possível moderar o afiliado.')
    );
  };

  const moderateAffiliates = (status: 'A' | 'R') => {
    modal.confirm({
      title: 'Atenção',
      content: `Deseja realmente ${status === 'A' ? 'aprovar' : 'reprovar'} os afiliados selecionados?`,
      okText: status === 'A' ? 'Aprovar' : 'Reprovar',
      cancelText: 'Cancelar',
      cancelButtonProps: { type: 'text' },
      okButtonProps: status === 'R' ? { danger: true } : undefined,
      onOk: () => {
        affiliateService.moderateMultipleAffiliates(affiliatesSelected, status).subscribe(
          () => {
            setAffiliatesSelected([]);
            message.success(`Afiliados ${status === 'A' ? 'aprovados' : 'reprovados'} com sucesso.`);
          },
          () => message.error('Não foi possível moderar os afiliados.')
        );
      }
    });
  };

  const handleSortClick = (field: string) => {
    const isAsc = currentSort?.field === field && currentSort?.direction === 'asc';
    onSort(field, isAsc ? 'desc' : 'asc');
  };

  const columns: any[] = [
    {
      title: () => (
        <PermissionHide action={'myeduzz:meus-afiliados:editar'}>
          <Checkbox
            indeterminate={!!affiliatesSelected?.length && affiliatesSelected?.length < affiliates?.length}
            checked={!!affiliates?.length && affiliatesSelected?.length === affiliates?.length}
            onChange={selectAllAffiliates}
            disabled={!affiliates?.length}
          />
        </PermissionHide>
      ),
      width: 45,
      render: (_: unknown, affiliate: IAffiliate) => (
        <PermissionHide action={'myeduzz:meus-afiliados:editar'}>
          <Checkbox checked={affiliatesSelected.includes(affiliate.id)} onClick={() => toggleSelect(affiliate.id)} />
        </PermissionHide>
      )
    },
    {
      title: 'Data da solicitação',
      width: 170,
      render: (_: unknown, affiliate: IAffiliate) => (
        <Typography.Text>{formatDate(affiliate.request_date)}</Typography.Text>
      ),
      onHeaderCell: () => ({ onClick: () => handleSortClick('request_date') })
    },
    {
      title: 'Produto',
      width: 300,
      render: (_: unknown, affiliate: IAffiliate) => (
        <div className='flex flex-col items-start gap-1'>
          <Typography.Text strong>
            <Truncate count={30} final='...'>
              {affiliate.product_title}
            </Truncate>
          </Typography.Text>
          <Typography.Text type='secondary'>({affiliate.product_id})</Typography.Text>
        </div>
      ),
      onHeaderCell: () => ({ onClick: () => handleSortClick('product_title') })
    },
    {
      title: 'Afiliado',
      render: (_: unknown, affiliate: IAffiliate) => (
        <div className='flex flex-col items-start gap-1'>
          <Link to={`/producer/detail/${affiliate.affiliate_id}`} target='_blank' rel='noopener noreferrer'>
            <Truncate count={25} final={'...'}>
              {affiliate.affiliate_name}
            </Truncate>
          </Link>
          <Typography.Text type='secondary'>{affiliate.affiliate_email}</Typography.Text>
        </div>
      ),
      onHeaderCell: () => ({ onClick: () => handleSortClick('affiliate_name') })
    },
    {
      title: 'Mensagem',
      width: 450,
      render: (_: unknown, affiliate: IAffiliate) => (
        <Typography.Text type='secondary'>
          <Truncate count={50} final='...'>
            {affiliate.request_message || '-'}
          </Truncate>
        </Typography.Text>
      )
    },
    {
      title: 'Ações',
      width: 130,
      fixed: 'right',
      align: 'center' as const,
      render: (_: unknown, affiliate: IAffiliate) => (
        <PermissionHide action={'myeduzz:meus-afiliados:editar'}>
          <Tooltip title='Aprovar' placement='top'>
            <Button
              type='text'
              shape='circle'
              icon={<Icon.ThumbsUp />}
              onClick={() => handleClickModerate(affiliate.id, 'A')}
            />
          </Tooltip>
          <Tooltip title='Reprovar' placement='top'>
            <Button
              type='text'
              shape='circle'
              icon={<Icon.ThumbsDown />}
              onClick={() => handleClickModerate(affiliate.id, 'R')}
            />
          </Tooltip>
        </PermissionHide>
      )
    }
  ];

  return (
    <>
      {!!affiliatesSelected.length && (
        <div className='mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md bg-(--uizz-antd-color-bg-container) p-4 shadow-sm'>
          <Typography.Text strong>{affiliatesSelected.length} selecionado(s)</Typography.Text>
          <div className='flex flex-wrap gap-2'>
            <Button onClick={resetAffiliatesSelected}>Cancelar</Button>
            <Button icon={<Icon.ThumbsDown />} onClick={() => moderateAffiliates('R')}>
              Reprovar
            </Button>
            <Button type='primary' icon={<Icon.ThumbsUp />} onClick={() => moderateAffiliates('A')}>
              Aprovar
            </Button>
          </div>
        </div>
      )}

      <div>
        <Table
          id='affiliates_table'
          size='small'
          sticky
          className='[&_.ant-table-tbody_.ant-typography]:text-sm'
          loading={isLoading}
          columns={columns}
          dataSource={affiliates || []}
          scroll={{ x: 'max-content' }}
          rowKey='id'
          pagination={false}
        />
        {!isLoading && pagination && (
          <div className='mt-4 flex justify-end'>
            <Pagination
              current={pagination.current_page || 1}
              pageSize={pagination.per_page || 10}
              total={pagination.total_items || 0}
              pageSizeOptions={['10', '20', '50', '100', '200']}
              onChange={onChangePage}
              onShowSizeChange={(_current, size) => onChangeRowsPerPage(size)}
              showSizeChanger
            />
          </div>
        )}
      </div>
    </>
  );
};

export default memo(TablePending);
